(() => {
  'use strict';

  const VARIANT_COOKIE = 'riad_popup_variant';
  const SEEN_COOKIE = 'riad_popup_seen';
  const LEGACY_COOKIE = 'nwSignup';
  const SESSION_START = 'riad_popup_session_start';
  const SESSION_ID = 'riad_popup_session_id';
  const FIRST_PATH = 'riad_popup_first_path';
  const PRODUCT_PATHS = 'riad_popup_product_paths';
  const ASSIGNED_EVENT = 'riad_popup_assigned_event';

  const getCookieValue = (name) => {
    const prefix = encodeURIComponent(name) + '=';
    const item = document.cookie.split('; ').find((part) => part.startsWith(prefix));
    return item ? decodeURIComponent(item.slice(prefix.length)) : '';
  };

  const setPopupCookie = (name, value, days) => {
    const expires = new Date(Date.now() + days * 86400000).toUTCString();
    document.cookie = encodeURIComponent(name) + '=' + encodeURIComponent(value) +
      '; expires=' + expires + '; path=/; SameSite=Lax; Secure';
  };

  const sessionGet = (key) => {
    try { return sessionStorage.getItem(key); } catch (error) { return null; }
  };

  const sessionSet = (key, value) => {
    try { sessionStorage.setItem(key, value); } catch (error) {}
  };

  const randomId = () => {
    if (window.crypto && crypto.getRandomValues) {
      const values = new Uint32Array(2);
      crypto.getRandomValues(values);
      return values[0].toString(36) + values[1].toString(36);
    }
    return Math.random().toString(36).slice(2) + Date.now().toString(36);
  };

  class RiadNewsletterPopup extends HTMLElement {
    connectedCallback() {
      if (this.initialized) return;
      this.initialized = true;
      this.opened = false;
      this.pendingTimer = null;
      this.pageType = this.dataset.pageType || '';
      this.frequencyDays = Number(this.dataset.frequencyDays || 30);
      this.minimumSessionMs = Number(this.dataset.minSession || 15000);
      this.controlDelayMs = Number(this.dataset.controlDelay || 15000);
      this.productThreshold = Number(this.dataset.productThreshold || 3);
      this.mobileDepth = Number(this.dataset.mobileDepth || 60);
      this.customerSubscribed = this.dataset.customerSubscribed === 'true';
      this.initialCartCount = Number(this.dataset.cartCount || 0);
      this.isMobile = window.matchMedia('(max-width: 767px)').matches;
      this.sessionStart = this.getSessionStart();
      this.sessionId = this.getSessionId();
      this.firstPath = this.getFirstPath();
      this.variant = this.getVariant();
      this.dataset.runtime = 'ready';
      this.dataset.variant = this.variant;

      this.applyVariantToAnalytics();
      if (!sessionGet(ASSIGNED_EVENT)) {
        this.track('session_assigned');
        sessionSet(ASSIGNED_EVENT, '1');
      }

      this.declineButton = this.querySelector('[data-newsletter-decline]');
      this.form = this.querySelector('form');
      this.declineButton?.addEventListener('click', () => this.close('declined'));
      this.form?.addEventListener('submit', () => this.track('form_submitted'));

      if (this.querySelector('.form-success')) {
        this.markSubscribed();
        this.openConfirmation();
        return;
      }
      if (this.isSuppressed() || window.Shopify?.designMode) return;
      if (this.variant === 'A') this.startControl();
      else this.startIntentVariant();
    }

    getSessionStart() {
      const stored = Number(sessionGet(SESSION_START));
      if (stored > 0) return stored;
      const started = Date.now();
      sessionSet(SESSION_START, String(started));
      return started;
    }

    getSessionId() {
      const stored = sessionGet(SESSION_ID);
      if (stored) return stored;
      const id = randomId();
      sessionSet(SESSION_ID, id);
      return id;
    }

    getFirstPath() {
      const stored = sessionGet(FIRST_PATH);
      if (stored) return stored;
      sessionSet(FIRST_PATH, window.location.pathname);
      return window.location.pathname;
    }

    getVariant() {
      const existing = getCookieValue(VARIANT_COOKIE);
      if (existing === 'A' || existing === 'B') return existing;
      const value = window.crypto && crypto.getRandomValues
        ? (crypto.getRandomValues(new Uint8Array(1))[0] % 2 === 0 ? 'A' : 'B')
        : (Math.random() < 0.5 ? 'A' : 'B');
      setPopupCookie(VARIANT_COOKIE, value, 45);
      return value;
    }

    applyVariantToAnalytics() {
      window.dataLayer = window.dataLayer || [];
      window.dataLayer.push({event:'riad_popup_variant',riad_popup_variant:this.variant,riad_popup_session_id:this.sessionId});
      if (typeof window.gtag === 'function') window.gtag('set','user_properties',{riad_popup_variant:this.variant});
    }

    track(action, details = {}) {
      const payload = {experiment:'intent_popup_v1',variant:this.variant,action,session_id:this.sessionId,page_type:this.pageType,path:window.location.pathname,timestamp:new Date().toISOString(),...details};
      window.dataLayer = window.dataLayer || [];
      window.dataLayer.push({event:'riad_popup_experiment',...payload});
      if (typeof window.gtag === 'function') window.gtag('event','riad_popup_experiment',payload);
      if (typeof window.Shopify?.analytics?.publish === 'function') window.Shopify.analytics.publish('riad_popup_experiment',payload);
      window.dispatchEvent(new CustomEvent('riad:popup-experiment',{detail:payload}));
    }

    isSuppressed() {
      if (this.pageType === 'cart' || window.location.pathname.startsWith('/checkout')) return true;
      if (this.initialCartCount > 0 || this.customerSubscribed) return true;
      if (getCookieValue(SEEN_COOKIE)) return true;
      return ['closed','subscribed'].includes(getCookieValue(LEGACY_COOKIE));
    }

    startControl() {
      if (this.pageType !== 'index') return;
      const elapsed = Date.now() - this.sessionStart;
      const wait = Math.max(this.controlDelayMs,this.minimumSessionMs - elapsed,0);
      window.setTimeout(() => this.tryOpen('control_timer'),wait);
    }

    startIntentVariant() {
      const firstLandingIsHome = this.firstPath === '/' || this.firstPath === '';
      if (this.pageType === 'index' && firstLandingIsHome) return;
      const viewedProducts = this.recordProductView();
      if (viewedProducts >= this.productThreshold) this.requestOpen('three_product_pages');
      if (this.isMobile) this.bindMobileIntent();
      else this.bindDesktopExit();
    }

    recordProductView() {
      let paths = [];
      try { paths = JSON.parse(sessionGet(PRODUCT_PATHS) || '[]'); } catch (error) {}
      if (this.pageType === 'product' && !paths.includes(window.location.pathname)) {
        paths.push(window.location.pathname);
        sessionSet(PRODUCT_PATHS,JSON.stringify(paths.slice(-20)));
      }
      return paths.length;
    }

    bindDesktopExit() {
      this.exitHandler = (event) => {
        if (event.relatedTarget === null && event.clientY <= 8) this.requestOpen('desktop_exit_intent');
      };
      document.addEventListener('mouseout',this.exitHandler,{passive:true});
    }

    bindMobileIntent() {
      let previousY = window.scrollY;
      let previousAt = performance.now();
      let maxDepth = 0;
      this.scrollHandler = () => {
        const now = performance.now();
        const currentY = window.scrollY;
        const scrollable = Math.max(document.documentElement.scrollHeight - window.innerHeight,1);
        maxDepth = Math.max(maxDepth,(currentY / scrollable) * 100);
        const upwardDistance = previousY - currentY;
        const elapsed = now - previousAt;
        if (maxDepth >= this.mobileDepth && upwardDistance >= 120 && elapsed <= 350) this.requestOpen('mobile_rapid_upscroll');
        if (elapsed >= 80 || Math.abs(currentY - previousY) >= 40) {
          previousY = currentY;
          previousAt = now;
        }
      };
      window.addEventListener('scroll',this.scrollHandler,{passive:true});
    }

    requestOpen(trigger) {
      if (this.opened || this.pendingTimer || this.isSuppressed()) return;
      const remaining = this.minimumSessionMs - (Date.now() - this.sessionStart);
      if (remaining > 0) {
        this.pendingTimer = window.setTimeout(() => {
          this.pendingTimer = null;
          this.tryOpen(trigger);
        },remaining + 50);
        return;
      }
      this.tryOpen(trigger);
    }

    async hasCartItems() {
      if (this.initialCartCount > 0) return true;
      try {
        const cartUrl = (window.routes?.cart_url || '/cart').replace(/\.js$/,'') + '.js';
        const response = await fetch(cartUrl,{credentials:'same-origin',headers:{Accept:'application/json'}});
        if (!response.ok) return false;
        const cart = await response.json();
        return Number(cart.item_count || 0) > 0;
      } catch (error) {
        return this.initialCartCount > 0;
      }
    }

    async tryOpen(trigger) {
      if (this.opened || this.isSuppressed() || document.visibilityState === 'hidden') return;
      if (Date.now() - this.sessionStart < this.minimumSessionMs) {
        this.requestOpen(trigger);
        return;
      }
      if (await this.hasCartItems()) {
        this.track('suppressed_cart',{trigger});
        return;
      }
      this.open(trigger);
    }

    open(trigger, confirmation = false) {
      if (this.opened || typeof window.tingle?.modal !== 'function') return;
      this.opened = true;
      this.confirmation = confirmation;
      if (!confirmation) {
        setPopupCookie(SEEN_COOKIE,'1',this.frequencyDays);
        this.track('popup_shown',{trigger});
      }
      this.modal = new window.tingle.modal({
        cssClass:['nwPopup','riadPopupExperiment','riadVariant' + this.variant],
        closeLabel:'Pop-up sluiten',
        onClose:() => {
          if (!this.confirmation) this.suppress(this.closeReason || 'closed');
          this.cleanup();
        }
      });
      this.modal.setContent(this);
      this.modal.open();
      this.escapeHandler = (event) => {
        if (event.key === 'Escape') this.close('escape');
      };
      document.addEventListener('keydown',this.escapeHandler);
    }

    openConfirmation() {
      window.setTimeout(() => this.open('subscription_confirmation',true),100);
    }

    close(reason) {
      if (!this.modal) return;
      if (!this.confirmation) {
        this.closeReason = reason;
        this.track('popup_closed',{reason});
      }
      this.modal.close();
    }

    suppress(value) {
      setPopupCookie(SEEN_COOKIE,'1',this.frequencyDays);
      setPopupCookie(LEGACY_COOKIE,'closed',this.frequencyDays);
      this.track('visitor_suppressed',{reason:value});
    }

    markSubscribed() {
      setPopupCookie(SEEN_COOKIE,'1',this.frequencyDays);
      setPopupCookie(LEGACY_COOKIE,'subscribed',this.frequencyDays);
      this.track('popup_subscribed');
    }

    cleanup() {
      if (this.exitHandler) document.removeEventListener('mouseout',this.exitHandler);
      if (this.scrollHandler) window.removeEventListener('scroll',this.scrollHandler);
      if (this.escapeHandler) document.removeEventListener('keydown',this.escapeHandler);
      if (this.pendingTimer) window.clearTimeout(this.pendingTimer);
    }
  }

  if (!customElements.get('riad-newsletter-popup')) customElements.define('riad-newsletter-popup',RiadNewsletterPopup);
})();
